/**
 * selling/sellingUtils.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared constants and utility functions used across SellingTab sub-components.
 *
 * EXPORTS:
 *   FALLBACK_IMG   — base64 SVG placeholder shown when an ad image fails to load
 *   handleImgError — onError handler for <img> elements
 *   VIDEO_EXTS     — list of video file extensions
 *   isVideoUrl     — returns true if a URL points to a video file
 *   Spinner        — inline loading spinner component
 *   StarRating     — star rating display component
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React from 'react';

export const FALLBACK_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='260' " +
  "viewBox='0 0 400 260'%3E%3Crect width='400' height='260' fill='%23f0f0f0'/%3E" +
  "%3Crect x='162' y='85' width='76' height='60' rx='6' fill='%23d0d0d0'/%3E" +
  "%3Ccircle cx='181' cy='103' r='9' fill='%23bbb'/%3E" +
  "%3Cpolygon points='162,145 186,115 206,130 230,105 238,145' fill='%23c8c8c8'/%3E" +
  "%3Ctext x='200' y='185' font-family='Arial' font-size='13' fill='%23aaa' " +
  "text-anchor='middle'%3ENo Image%3C/text%3E%3C/svg%3E";

export const handleImgError = (e) => {
  if (e.target.src !== FALLBACK_IMG) e.target.src = FALLBACK_IMG;
};

export const VIDEO_EXTS = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];

/** Safely extract a URL string from an image/video entry that may be an object or a plain string */
export const extractUrl = (entry) => {
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  // AdImage / AdVideo object shape: { image, url, file, src, video, ... }
  return entry.image || entry.video || entry.url || entry.file || entry.src || null;
};

export const isVideoUrl = (url) => {
  const str = extractUrl(url); // handles both string and object inputs
  if (!str || typeof str !== 'string') return false;
  const lower = str.toLowerCase();
  return VIDEO_EXTS.some(ext => lower.includes(ext));
};

// ── Inline spinner ────────────────────────────────────────────────────────────
export const Spinner = ({ color = '#fff' }) => (
  <span style={{
    width: 14, height: 14,
    border: `2px solid ${color}33`,
    borderTop: `2px solid ${color}`,
    borderRadius: '50%', display: 'inline-block',
    animation: 'spin 0.7s linear infinite', flexShrink: 0,
  }} />
);

// ── Star rating ───────────────────────────────────────────────────────────────
export const StarRating = ({ rating = 0, count = 0 }) => {
  const stars = Math.round(rating);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '5px' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} width="12" height="12" viewBox="0 0 24 24" fill={i <= stars ? '#f59e0b' : '#d1d5db'}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
      {count > 0 && (
        <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '2px' }}>
          ({count} review{count !== 1 ? 's' : ''})
        </span>
      )}
    </div>
  );
};

// ── Item card (grid) ──────────────────────────────────────────────────────────
